import React from "react";
import { Modal, Box, IconButton, Fade, Backdrop } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

interface BottomModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
}

const BottomModal: React.FC<BottomModalProps> = ({
  open,
  onClose,
  children,
  title,
  showCloseButton = true,
  closeOnBackdropClick = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const modalStyle = {
    position: "fixed" as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    borderTopLeftRadius: isMobile ? "16px" : "20px",
    borderTopRightRadius: isMobile ? "16px" : "20px",
    maxHeight: "80vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
    boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.15)",
    transform: open ? "translateY(0)" : "translateY(100%)",
    transition: "transform 0.3s ease-in-out",
  };

  const headerStyle = {
    padding: isMobile ? "16px 20px 12px" : "20px 24px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: "auto",
    flexShrink: 0,
  };

  const contentStyle = {
    flex: 1,
    overflow: "auto",
    padding: isMobile ? "16px 20px" : "20px 24px",
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeOnBackdropClick ? onClose : onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
      sx={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <>
        <Fade in={open} timeout={300}>
          <Box
            sx={modalStyle}
            onClick={handleBackdropClick}
          >
            {(title || showCloseButton) && (
              <Box sx={headerStyle}>
                {title && (
                  <Box
                    component="h2"
                    sx={{
                      margin: 0,
                      fontSize: isMobile ? "18px" : "20px",
                      fontWeight: 600,
                      color: "#333333",
                      lineHeight: 1.2,
                    }}
                  >
                    {title}
                  </Box>
                )}
              </Box>
            )}

            {/* Handle bar for mobile */}
            {isMobile && (
              <Box
                sx={{
                  position: "absolute",
                  top: "8px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "40px",
                  height: "4px",
                  backgroundColor: "#e0e0e0",
                  borderRadius: "2px",
                  cursor: "pointer",
                }}
                onClick={onClose}
              />
            )}

            <Box sx={contentStyle}>
              {children}
            </Box>
            {/* Close button */}
            <Box mt={2} ml={2} mr={2} mb={2} textAlign="center">
              <Box
                component="button"
                onClick={onClose}
                style={{
                  backgroundColor: "#00B8A9",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px 20px",
                  cursor: "pointer",
                  width: "100%",
                  fontWeight: 600,
                }}
              >
                Close
              </Box>
            </Box>
          </Box>
        </Fade>
      </>
    </Modal>
  );
};

export default BottomModal;
